import React, { useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import TextAreaField from "../../../components/forms/textarea-field";
import AddButton from "../../../components/buttons/button";
import TransSummaryComp from "../../../components/helper-components/trans-summary-comp";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";
import ConfirmModal from "../../../components/helper-components/confirm-modal";
import { UpdatePickList } from "../../../redux/actions/sales-action";
import { generateUuid } from "../../../utils/uuid";
import ArrowDownLeftIcon from "../../../assets/icons/sales-icons/arrow-down-left-icon";
import ClockIcon from "../../../assets/icons/sales-icons/clock-icon";

const PickListFooter = ({
  disabled = false,
  pickListId,
  warehouseId,
  page = "",
  status,
}) => {
  const { values } = useFormikContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [activeButton, setActiveButton] = useState("");
  const [showPartialConfirm, setShowPartialConfirm] = useState(false);

  const requestIdRef = useRef(null);
  if (!requestIdRef.current) requestIdRef.current = generateUuid();

  const items = values?.items || [];
  const pendingRows = items.filter((row) => !row?.already_picked);
  const totalQty = pendingRows.reduce(
    (sum, row) => sum + (row?.qty_to_pick_base || 0),
    0
  );
  const totalBins = new Set(
    pendingRows.map((row) => row?.from_bin_id).filter(Boolean)
  ).size;

  const buildPayload = (isPartial) => {
    const checkedItems = pendingRows.filter((row) => row?.picked);
    return {
      picklist_id: pickListId,
      picklist_data: checkedItems.map((row) => ({
        warehouse_id: warehouseId,
        sott_id: row.sott_id,
        item_id: row.item_id,
        required_qty: row.qty_to_pick_base,
        picked_qty:
          (parseFloat(row.actual_qty_picked) || 0) * (row.uom_ratio || 1),
        from_bin_id: row.from_bin_id,
        item_batch_number: row.item_batch_number,
        uom_id: row.uom_id,
      })),
      is_partial: isPartial,
      picker_id: values?.picker_id ?? null,
      warehouse_id: warehouseId,
      remarks: values?.pl_remarks ?? "",
      request_id: requestIdRef.current,
    };
  };

  const onSuccess = (data) => {
    setButtonLoading(false);
    successToast(data?.message || "Picklist updated successfully");
    navigate("/picklist-table");
  };

  const onError = (data) => {
    setButtonLoading(false);
    errorToast(data?.data?.message || "Failed to update picklist");
  };

  const submitPayload = (isPartial) => {
    const payload = buildPayload(isPartial);
    if (!payload) {
      errorToast("Select at least one picked item before continuing");
      return;
    }
    setActiveButton(isPartial ? "partial" : "save");
    setButtonLoading(true);
    dispatch(UpdatePickList(payload, onSuccess, onError));
  };

  const handleSubmit = (isPartial) => {
    if (buttonLoading) return;
    if (!pickListId) {
      errorToast("Picklist is not ready yet");
      return;
    }
    if (!values?.picker_id) {
      errorToast("Please select a picker before continuing");
      return;
    }
    if (!isPartial && pendingRows.some((row) => !row?.picked)) {
      setShowPartialConfirm(true);
      return;
    }
    submitPayload(isPartial);
  };

  const statCards = [
    {
      conClass:
        "bg-[var(--trans-stats-con-green-bg)] rounded-[12px] h-[5.3rem] w-full sm:w-[220px] p-3 flex justify-between",
      label: "Total Qty To Pick",
      value: `${totalQty}`,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
      icon: <ArrowDownLeftIcon />,
    },
    {
      conClass:
        "bg-[var(--trans-stats-con-blue-bg)] rounded-[12px] h-[5.3rem] w-full sm:w-[220px] p-3 flex justify-between",
      label: "Total Bins",
      value: `${totalBins}`,
      labelClass:
        "text-[length:var(--trans-stats-con-label-fs)] font-[var(--trans-stats-con-label-fw)] text-[var(--trans-stats-con-label-fg)]",
      valueClass:
        "text-[length:var(--trans-stats-con-value-fs)] font-[var(--trans-stats-con-value-fw)] text-[var(--trans-stats-con-value-fg)] mt-2",
      icon: <ClockIcon />,
    },
  ];

  if (page === "view") {
    return (
      <div className="mx-5 mt-4 bg-white">
        <div className="flex items-end gap-4 flex-wrap border-t border-[var(--create-table-footer-border)] pt-4 pb-4">
          {statCards.map((sum, idx) => (
            <TransSummaryComp key={idx} sum={sum} />
          ))}

          {(status === "partial") && (
            <div className="flex gap-3 flex-wrap shrink-0 ml-auto">
              <AddButton
                prefixIcon={false}
                text={"Edit Pick List"}
                type="button"
                onclkFn={() => navigate(`/edit-picklist/${pickListId}`)}
                className="flex items-center justify-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-5 mt-4 bg-white">
      <div className="flex items-end gap-4 flex-wrap border-t border-[var(--create-table-footer-border)] pt-4 pb-4">
        {statCards.map((sum, idx) => (
          <TransSummaryComp key={idx} sum={sum} />
        ))}

        <div className="flex-1 min-w-[200px] flex flex-col">
          <label className="min-h-[1rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]">
            Remarks
          </label>
          <TextAreaField
            isContainer={false}
            name="pl_remarks"
            placeholder="Enter your remarks here..."
            isDisabled={disabled}
            maxLength={250}
            rows={2}
            feildHeight="4rem"
            resize="none"
            errMsgContainerClass="hidden"
          />
        </div>

        {!disabled && (
          <div className="flex gap-3 flex-wrap shrink-0">
            <AddButton
              isDisabled={buttonLoading}
              loading={buttonLoading && activeButton === "partial"}
              prefixIcon={false}
              text={"Partial Pick"}
              type="button"
              onclkFn={() => handleSubmit(true)}
              className="flex items-center justify-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--draft-button-bg)] cursor-pointer text-[var(--draft-button-fg)] border border-[var(--draft-button-border)] text-[length:var(--draft-button-fs)] font-[var(--draft-button-fw)]"
            />
            <AddButton
              isDisabled={buttonLoading}
              loading={buttonLoading && activeButton === "save"}
              prefixIcon={false}
              text={"Close Pick"}
              type="button"
              onclkFn={() => handleSubmit(false)}
              className="flex items-center justify-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]"
            />
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showPartialConfirm}
        title="Close Pick List?"
        message="Not all items have been picked yet. Do you want to close this pick list?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          setShowPartialConfirm(false);
          submitPayload(false);
        }}
        onCancel={() => {
          setShowPartialConfirm(false);
          submitPayload(true);
        }}
      />
    </div>
  );
};

export default PickListFooter;
